import React, { useState } from 'react';
import { ArrowLeft, Wallet, CreditCard, Check } from 'lucide-react';
import { getProductImage } from '../../utils/imageHelpers';

// Steps: 'products' → 'plans' → 'payment'

const SubscriptionModal = ({
  isOpen,
  onClose,
  subscriptionPlans,
  showSubscription,         // object = specific product; true = open list
  subscribableProducts,
  subscribeProduct,
  editSubscription,
  updateSubscriptionPlan,
  isLoading = false,
  walletBalance = 0,
  razorpayEnabled = false,
}) => {
  const directProduct =
    showSubscription && typeof showSubscription === 'object' ? showSubscription : null;

  const [selectedProduct, setSelectedProduct] = useState(() => directProduct);
  const [selectedPlan,    setSelectedPlan]    = useState(null);
  const [paymentMethod,   setPaymentMethod]   = useState('wallet');

  if (!isOpen) return null;

  const isEdit = !!(editSubscription && updateSubscriptionPlan);

  // ── Price helpers ────────────────────────────────────────────────────────────
  const getProductPrices = (product) => {
    const mrp       = parseFloat(product?.price || 0);
    const salePrice = parseFloat(product?.sale_price || mrp);
    const subPct    = parseFloat(product?.subscription_discount_percentage || 0);
    const subFixed  = parseFloat(product?.subscription_price || 0);
    // Effective subscription price: use the dedicated field if set, else apply percentage off sale price
    const subPrice  = subFixed > 0 ? subFixed : subPct > 0 ? salePrice * (1 - subPct / 100) : salePrice;
    return { mrp, salePrice, subPrice };
  };

  // ── Navigation helpers ───────────────────────────────────────────────────────
  const goBack = () => {
    if (selectedPlan) {
      setSelectedPlan(null);       // payment → plan
    } else if (selectedProduct && !directProduct) {
      setSelectedProduct(null);    // plan → product list
    } else {
      onClose();                   // plan → close (opened from product card)
    }
  };

  // ── Derive current step ──────────────────────────────────────────────────────
  const step = selectedPlan ? 'payment' : selectedProduct ? 'plans' : 'products';

  // ── Header ───────────────────────────────────────────────────────────────────
  const Header = ({ subtitle }) => (
    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-5 rounded-b-3xl">
      <div className="flex items-center gap-3">
        <button
          onClick={goBack}
          className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all flex-shrink-0"
        >
          <ArrowLeft size={22} />
        </button>
        <div>
          <h1 className="text-xl font-bold leading-tight">Subscribe &amp; Save</h1>
          <p className="text-blue-100 text-xs mt-0.5">{subtitle}</p>
        </div>
      </div>
    </div>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // STEP 1 — Product list
  // ────────────────────────────────────────────────────────────────────────────
  if (step === 'products') {
    const list = Array.isArray(subscribableProducts) ? subscribableProducts : [];
    return (
      <div className="min-h-full bg-gray-50">
        <Header subtitle="Choose a product to subscribe" />
        <div className="p-4">
          {list.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <span className="text-5xl block mb-3">🔔</span>
              <p className="font-medium">No subscribable products available</p>
            </div>
          ) : (
            <div className="space-y-3">
              {list.map(product => {
                const { mrp, salePrice, subPrice } = getProductPrices(product);
                const hasSaleDiscount = salePrice < mrp;
                const hasSubDiscount  = subPrice  < salePrice;
                return (
                  <div
                    key={product.id}
                    className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm border border-gray-100 cursor-pointer hover:border-blue-300 active:scale-[0.99] transition-all"
                    onClick={() => setSelectedProduct(product)}
                  >
                    <div className="flex-shrink-0">
                      {getProductImage(product, 'large')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 truncate text-sm">
                        {product.name || product.product_name}
                      </p>
                      {product.description && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">{product.description}</p>
                      )}
                      <div className="flex items-baseline gap-2 mt-1 flex-wrap">
                        {hasSaleDiscount && (
                          <span className="text-xs text-gray-400 line-through">₹{mrp.toFixed(2)}</span>
                        )}
                        <span className="text-sm font-bold text-gray-700">₹{salePrice.toFixed(2)}</span>
                        {hasSubDiscount && (
                          <span className="text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                            Sub ₹{subPrice.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="flex-shrink-0 bg-blue-50 text-blue-600 text-xs font-medium px-3 py-1 rounded-full">
                      Subscribe
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────────
  // STEP 2 — Plan selection
  // ────────────────────────────────────────────────────────────────────────────
  if (step === 'plans') {
    const { mrp, salePrice, subPrice } = getProductPrices(selectedProduct);
    const hasSaleDiscount = salePrice < mrp;

    return (
      <div className="min-h-full bg-gray-50">
        <Header subtitle="Choose a plan and save on every delivery" />
        <div className="p-4">

          {/* Product card with full pricing */}
          <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm border border-gray-100 flex items-center gap-4">
            <div className="flex-shrink-0">
              {getProductImage(selectedProduct, 'xl')}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-800 text-base">
                {selectedProduct.name || selectedProduct.product_name}
              </p>
              {selectedProduct.description && (
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{selectedProduct.description}</p>
              )}
              <div className="mt-2 space-y-0.5">
                {/* MRP row */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-16">MRP</span>
                  <span className={`text-sm ${hasSaleDiscount ? 'line-through text-gray-400' : 'font-semibold text-gray-700'}`}>
                    ₹{mrp.toFixed(2)}
                  </span>
                </div>
                {/* Selling price row */}
                {hasSaleDiscount && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-16">Price</span>
                    <span className="text-sm font-semibold text-gray-700">₹{salePrice.toFixed(2)}</span>
                    <span className="text-xs text-orange-500 font-medium">
                      {Math.round(((mrp - salePrice) / mrp) * 100)}% off
                    </span>
                  </div>
                )}
                {/* Subscription price row */}
                {subPrice < salePrice && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-16">Sub price</span>
                    <span className="text-sm font-bold text-green-600">₹{subPrice.toFixed(2)}</span>
                    <span className="text-xs text-green-600 font-medium">
                      {Math.round(((salePrice - subPrice) / salePrice) * 100)}% off
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Plan cards */}
          <h3 className="font-semibold text-gray-700 text-sm mb-2">Choose a Plan</h3>
          <div className="space-y-3 mb-4">
            {subscriptionPlans.map(plan => {
              // Discounted per-delivery price on top of subscription price
              const perDelivery = subPrice * (1 - plan.discount / 100);
              const deliveries  = plan.id === 'daily' ? 30 : plan.id === 'alternate' ? 15 : plan.duration;
              const totalCost   = perDelivery * deliveries;
              const mrpTotal    = mrp * deliveries;
              const totalSaving = mrpTotal - totalCost;

              return (
                <div
                  key={plan.id}
                  className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-blue-400 transition-all cursor-pointer"
                  onClick={() => !isEdit && setSelectedPlan(plan)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-bold text-gray-800">{plan.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{plan.duration} days • {plan.discount}% off</p>
                    </div>
                    <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-bold">
                      Save {plan.discount}%
                    </span>
                  </div>

                  {/* Per-delivery pricing */}
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-xs text-gray-400 line-through">₹{mrp.toFixed(2)}/delivery</span>
                    <span className="text-lg font-bold text-blue-600">₹{perDelivery.toFixed(2)}/delivery</span>
                  </div>

                  {/* Period total */}
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-xs text-gray-500">Total: </span>
                      <span className="text-sm font-semibold text-gray-700">₹{totalCost.toFixed(2)}</span>
                      {totalSaving > 0 && (
                        <span className="text-xs text-green-600 ml-1">(save ₹{totalSaving.toFixed(0)})</span>
                      )}
                    </div>
                    {isEdit ? (
                      <button
                        disabled={isLoading}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateSubscriptionPlan(editSubscription.id, { planId: plan.id });
                          onClose();
                        }}
                        className="bg-blue-500 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
                      >
                        {isLoading ? 'Updating...' : 'Update Plan'}
                      </button>
                    ) : (
                      <button
                        className="bg-blue-500 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-600"
                        onClick={(e) => { e.stopPropagation(); setSelectedPlan(plan); }}
                      >
                        Select
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Benefits */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
            <p className="font-semibold text-gray-700 text-sm mb-1.5">✨ Subscription Benefits</p>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Guaranteed daily/scheduled delivery</li>
              <li>• Skip or pause anytime</li>
              <li>• Priority customer support</li>
              <li>• Exclusive subscriber discounts</li>
              <li>• Free delivery always</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────────
  // STEP 3 — Payment method
  // ────────────────────────────────────────────────────────────────────────────
  const { mrp, salePrice, subPrice } = getProductPrices(selectedProduct);
  const perDelivery  = subPrice * (1 - selectedPlan.discount / 100);
  const deliveries   = selectedPlan.id === 'daily' ? 30 : selectedPlan.id === 'alternate' ? 15 : selectedPlan.duration;
  const totalCost    = perDelivery * deliveries;
  const walletSuficient = walletBalance >= totalCost;

  const paymentOptions = [
    {
      id: 'wallet',
      label: 'Pay from Wallet',
      icon: <Wallet size={20} className="text-blue-500" />,
      detail: `Balance: ₹${parseFloat(walletBalance).toFixed(2)}`,
      available: true,
      warn: !walletSuficient ? `Insufficient balance (short ₹${(totalCost - walletBalance).toFixed(2)})` : null,
    },
    ...(razorpayEnabled ? [{
      id: 'razorpay',
      label: 'Pay via Razorpay',
      icon: <CreditCard size={20} className="text-indigo-500" />,
      detail: 'UPI · Card · Net Banking',
      available: true,
      warn: null,
    }] : []),
  ];

  return (
    <div className="min-h-full bg-gray-50">
      <Header subtitle="Choose how to pay for this subscription" />
      <div className="p-4">

        {/* Order summary */}
        <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm border border-gray-100">
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Order Summary</p>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-shrink-0">{getProductImage(selectedProduct, 'medium')}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">
                {selectedProduct.name || selectedProduct.product_name}
              </p>
              <p className="text-xs text-gray-500">{selectedPlan.name} plan · {deliveries} deliveries</p>
            </div>
          </div>
          <div className="border-t border-gray-100 pt-2 space-y-1">
            <div className="flex justify-between text-xs text-gray-500">
              <span>MRP total ({deliveries} × ₹{mrp.toFixed(2)})</span>
              <span className="line-through">₹{(mrp * deliveries).toFixed(2)}</span>
            </div>
            {salePrice < mrp && (
              <div className="flex justify-between text-xs text-gray-500">
                <span>Sale price total</span>
                <span>₹{(salePrice * deliveries).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs text-gray-500">
              <span>Subscription discount ({selectedPlan.discount}%)</span>
              <span className="text-green-600">−₹{((subPrice - perDelivery) * deliveries).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm font-bold text-gray-800 pt-1 border-t border-gray-100">
              <span>Total</span>
              <span className="text-blue-600">₹{totalCost.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Payment method selector */}
        <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Payment Method</p>
        <div className="space-y-2 mb-4">
          {paymentOptions.map(opt => (
            <div
              key={opt.id}
              onClick={() => setPaymentMethod(opt.id)}
              className={`bg-white rounded-xl p-4 border-2 flex items-center gap-3 cursor-pointer transition-all ${
                paymentMethod === opt.id ? 'border-blue-500' : 'border-gray-200 hover:border-gray-300'
              } ${opt.warn ? 'opacity-80' : ''}`}
            >
              <div className="flex-shrink-0">{opt.icon}</div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 text-sm">{opt.label}</p>
                <p className={`text-xs mt-0.5 ${opt.warn ? 'text-orange-500' : 'text-gray-400'}`}>
                  {opt.warn || opt.detail}
                </p>
              </div>
              {paymentMethod === opt.id && (
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Confirm button */}
        <button
          disabled={isLoading || (paymentMethod === 'wallet' && !walletSuficient)}
          onClick={() => {
            subscribeProduct(selectedProduct, selectedPlan, paymentMethod);
            onClose();
          }}
          className="w-full py-3 rounded-xl text-sm font-bold text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isLoading
            ? 'Processing...'
            : `Confirm Subscription · ₹${totalCost.toFixed(2)}`}
        </button>

        {paymentMethod === 'wallet' && !walletSuficient && (
          <p className="text-center text-xs text-orange-500 mt-2">
            Please top up your wallet or choose Razorpay to continue.
          </p>
        )}
      </div>
    </div>
  );
};

export default SubscriptionModal;
